import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';

interface Point {
    x: number;
    y: number;
}

@Component({
    selector: 'app-loader',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './loader.component.html',
    styleUrl: './loader.component.scss'
})
export class LoaderComponent implements OnChanges {
    @Input() progress!: number;
    @Input() isClosed = false;
    @Input() bettingTimeInSeconds!: number;
    @Input() hurryUpTimeInSeconds!: number;
    @Input() ballShootInTimeInSeconds!: number;

    dashOffset = 0;
    yellowPath = '';
    orangePath = '';

    readonly centerX = 50;
    readonly centerY = 50;
    readonly radius = 45;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    readonly circumference = 2 * Math.PI * this.radius;

    private readonly fullCircle = 360;
    private readonly halfCircle = 180;
    private readonly startOffsetDegrees = 270;
    private readonly arcGap = 7;
    private readonly unit = 1;

    ngOnChanges(): void {
        const totalArcTime = this.bettingTimeInSeconds + this.ballShootInTimeInSeconds;
        const greenTime = this.bettingTimeInSeconds - this.hurryUpTimeInSeconds;

        const greenEndAngle = this.fullCircle - this.progress * this.fullCircle;

        this.dashOffset = this.circumference * (this.unit - this.progress);

        const orangeRange = {
            start: (greenTime / totalArcTime) * this.fullCircle,
            end: (this.bettingTimeInSeconds / totalArcTime) * this.fullCircle - this.arcGap - this.ballShootInTimeInSeconds
        };

        const yellowRange = {
            start: (this.bettingTimeInSeconds / totalArcTime) * this.fullCircle + this.arcGap - this.ballShootInTimeInSeconds,
            end: this.fullCircle - this.unit
        };

        this.orangePath = this.getVisibleArcPath(greenEndAngle, orangeRange.start, orangeRange.end);
        this.yellowPath = this.getVisibleArcPath(greenEndAngle, yellowRange.start, yellowRange.end);
    }

    private getVisibleArcPath(greenEndAngle: number, arcStart: number, arcEnd: number): string {
        if (greenEndAngle >= arcEnd) {
            return '';
        }

        const startAngle = Math.max(greenEndAngle, arcStart);
        return this.createArcPath(startAngle, arcEnd);
    }

    private createArcPath(startAngle: number, endAngle: number): string {
        const largeArc = endAngle - startAngle > this.halfCircle ? this.unit : 0;
        const start = this.polarToCartesian(startAngle);
        const end = this.polarToCartesian(endAngle);

        return `M ${start.x} ${start.y} A ${this.radius} ${this.radius} 0 ${largeArc} 0 ${end.x} ${end.y}`;
    }

    private polarToCartesian(angle: number): Point {
        const angleUnit = ((this.startOffsetDegrees - angle) * Math.PI) / this.halfCircle;

        return {
            x: this.centerX + this.radius * Math.cos(angleUnit),
            y: this.centerY + this.radius * Math.sin(angleUnit)
        };
    }
}
